<template>
  <div class="profile-selector-overlay">
    <div class="profile-selector-card">
      <div class="profile-header">
        <h2>Valitse opettajaprofiili</h2>
        <p>Kirjauduit sisään tilille "{{ user?.name || user?.email }}"</p>
      </div>

      <div class="profile-options">
        <div
          v-for="profile in availableProfiles"
          :key="profile.id"
          class="profile-option"
          @click="selectProfile(profile.id)"
        >
          <div class="profile-info">
            <h3>{{ profile.name }}</h3>
            <p>Opettajaprofiili</p>
          </div>
          <div class="profile-arrow">→</div>
        </div>

        <div class="profile-option create-new" @click="showCreateForm = true">
          <div class="profile-info">
            <h3>+ Luo uusi profiilin</h3>
            <p>Luo uusi opettajaprofiili</p>
          </div>
          <div class="profile-arrow">→</div>
        </div>
      </div>

      <!-- Create new profile form -->
      <div v-if="showCreateForm" class="create-form">
        <h3>Luo uusi opettajaprofiili</h3>
        <div class="form-group">
          <label for="profileName">Profiilin nimi:</label>
          <input
            id="profileName"
            v-model="newProfileName"
            type="text"
            placeholder="Esim. Anna, Jukka, Liisa..."
            maxlength="50"
            @keyup.enter="createProfile"
          />
        </div>
        <div class="form-actions">
          <button @click="showCreateForm = false" class="cancel-button">
            Peruuta
          </button>
          <button
            @click="createProfile"
            :disabled="!newProfileName.trim() || creating"
            class="create-button"
          >
            {{ creating ? "Luodaan..." : "Luo profiilin" }}
          </button>
        </div>
        <div v-if="createError" class="error-message">
          {{ createError }}
        </div>
      </div>

      <div v-if="error" class="error-message">
        {{ error }}
      </div>

      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <p>Asetetaan profiilin...</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import type { TeacherProfile, User } from "@/stores/types";

const authStore = useAuthStore();
const router = useRouter();

const user = computed((): User | null => authStore.user);
const availableProfiles = computed(
  (): TeacherProfile[] => authStore.availableProfiles,
);

const showCreateForm = ref(false);
const newProfileName = ref("");
const creating = ref(false);
const createError = ref("");
const loading = ref(false);
const error = ref("");

const selectProfile = async (profileId: number) => {
  try {
    loading.value = true;
    error.value = "";

    await authStore.selectProfile(profileId);

    // Navigate to assessments after successful profile selection
    await router.push({ name: "assessments" });
  } catch (err) {
    error.value =
      err instanceof Error ? err.message : "Virhe profiilia valittaessa";
  } finally {
    loading.value = false;
  }
};

const createProfile = async () => {
  if (!newProfileName.value.trim()) {
    createError.value = "Profiilin nimi on pakollinen";
    return;
  }

  try {
    creating.value = true;
    createError.value = "";

    const profile = await authStore.createProfile(
      newProfileName.value.trim(),
      {},
    );

    // Automatically select the newly created profile
    await selectProfile(profile.id);

    // Reset form
    showCreateForm.value = false;
    newProfileName.value = "";
  } catch (err) {
    createError.value =
      err instanceof Error ? err.message : "Virhe profiilia luotaessa";
  } finally {
    creating.value = false;
  }
};
</script>

<style scoped>
.profile-selector-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: var(--space-md);
}

.profile-selector-card {
  background: white;
  border-radius: var(--radius-lg);
  padding: var(--space-xl);
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: var(--shadow-lg);
}

.profile-header {
  text-align: center;
  margin-bottom: var(--space-xl);
}

.profile-header h2 {
  margin: 0 0 var(--space-sm) 0;
  color: var(--color-text-primary);
  font-size: 1.5rem;
  font-weight: 600;
}

.profile-header p {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: 0.875rem;
}

.profile-options {
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
  margin-bottom: var(--space-lg);
}

.profile-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-lg);
  border: 2px solid var(--color-border-light);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.profile-option:hover {
  border-color: var(--color-primary);
  background: var(--color-bg-tertiary);
  transform: translateY(-2px);
  box-shadow: var(--shadow-sm);
}

.profile-option.create-new {
  border-style: dashed;
  border-color: var(--color-text-light);
}

.profile-option.create-new:hover {
  border-color: var(--color-primary);
  border-style: solid;
}

.profile-info h3 {
  margin: 0 0 var(--space-xs) 0;
  color: var(--color-text-primary);
  font-size: 1.1rem;
  font-weight: 600;
}

.profile-info p {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: 0.875rem;
}

.profile-arrow {
  font-size: 1.5rem;
  color: var(--color-text-light);
  transition: color 0.2s ease-in-out;
}

.profile-option:hover .profile-arrow {
  color: var(--color-primary);
}

.create-form {
  border-top: 1px solid var(--color-border-light);
  padding-top: var(--space-lg);
  margin-bottom: var(--space-lg);
}

.create-form h3 {
  margin: 0 0 var(--space-md) 0;
  color: var(--color-text-primary);
  font-size: 1.1rem;
  font-weight: 600;
}

.form-group {
  margin-bottom: var(--space-md);
}

.form-group label {
  display: block;
  margin-bottom: var(--space-xs);
  font-weight: 500;
  color: var(--color-text-secondary);
  font-size: 0.875rem;
}

.form-group input {
  width: 100%;
  padding: var(--space-sm);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  font-size: 1rem;
  transition: border-color 0.2s ease-in-out;
}

.form-group input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px rgba(var(--color-primary-rgb), 0.1);
}

.form-actions {
  display: flex;
  gap: var(--space-md);
  justify-content: flex-end;
}

.cancel-button,
.create-button {
  padding: var(--space-sm) var(--space-lg);
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.2s ease-in-out;
}

.cancel-button {
  background: var(--color-bg-secondary);
  color: var(--color-text-secondary);
}

.cancel-button:hover {
  background: var(--color-bg-tertiary);
  color: var(--color-text-primary);
}

.create-button {
  background: var(--color-primary);
  color: white;
}

.create-button:hover:not(:disabled) {
  background: var(--color-primary-dark);
  transform: translateY(-1px);
}

.create-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.error-message {
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: var(--space-sm);
  padding: var(--space-sm);
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: var(--radius-sm);
}

.loading-state {
  text-align: center;
  padding: var(--space-lg);
}

.loading-spinner {
  display: inline-block;
  width: 30px;
  height: 30px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: var(--space-md);
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .profile-selector-card {
    padding: var(--space-lg);
    margin: var(--space-md);
  }

  .form-actions {
    flex-direction: column;
    gap: var(--space-sm);
  }

  .cancel-button,
  .create-button {
    width: 100%;
  }
}
</style>
